import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { listBoards, boardEmailAddress, rememberBoard, recallBoard, type BoardOption } from '../lib/boards';
import { listOrgMembers, type MemberOption } from '../lib/members';
import { readCurrentItem, type OutlookItem } from '../lib/outlookItem';
import { APP_BASE, SUPABASE_URL, INBOUND_DOMAIN } from '../lib/config';

/**
 * "Add to Flowza" — pick a board and owner, review, create.
 *
 * Subject and body are prefilled but editable: an email subject is often a poor
 * task title, and retyping it is worse than tidying it in place.
 *
 * The write goes to the `planner-addin-create-task` Edge Function, which
 * forwards the user's own JWT to an RPC that does task + thread link +
 * assignees in one transaction. Nothing here is trusted — this form is a
 * convenience over an authoritative server call.
 */

interface Created {
  task_id: string;
  task_number: string;
  title: string;
  organization_id: string;
  board_id: string;
  threaded: boolean;
}

export default function CreateTaskPane({ email, onSignedOut }: { email: string; onSignedOut: () => void }) {
  const [boards, setBoards] = useState<BoardOption[]>([]);
  const [boardId, setBoardId] = useState('');
  const [members, setMembers] = useState<MemberOption[]>([]);
  const [owner, setOwner] = useState('');
  const [extraMembers, setExtraMembers] = useState<string[]>([]);
  const [boardAddress, setBoardAddress] = useState<string | null>(null);
  const [item, setItem] = useState<OutlookItem | null>(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [due, setDue] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<Created | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [{ boards: bs, error: bErr }, it] = await Promise.all([listBoards(), readCurrentItem()]);
      if (cancelled) return;
      setBoards(bs); setLoadError(bErr); setItem(it);
      setTitle(it?.subject ?? ''); setBody(it?.body ?? '');
      const last = recallBoard();
      setBoardId(bs.find((b) => b.id === last)?.id ?? (bs.length === 1 ? bs[0].id : ''));
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  // Members and the board address follow the selected board's organisation.
  const board = boards.find((b) => b.id === boardId);
  const orgId = board?.organization_id ?? '';
  useEffect(() => {
    let cancelled = false;
    if (!orgId) { setMembers([]); setOwner(''); setExtraMembers([]); return; }
    (async () => {
      const ms = await listOrgMembers(orgId);
      if (!cancelled) setMembers(ms);
    })();
    return () => { cancelled = true; };
  }, [orgId]);

  useEffect(() => {
    let cancelled = false;
    if (!boardId) { setBoardAddress(null); return; }
    (async () => {
      const addr = await boardEmailAddress(boardId, INBOUND_DOMAIN);
      if (!cancelled) setBoardAddress(addr);
    })();
    return () => { cancelled = true; };
  }, [boardId]);

  const toggleMember = useCallback((id: string) => {
    setExtraMembers((prev) => (prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id]));
  }, []);

  const create = async () => {
    if (!boardId || !title.trim()) return;
    setBusy(true); setError(null);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
      if (!token) { setError('Your session expired. Sign out and back in.'); setBusy(false); return; }

      const res = await fetch(`${SUPABASE_URL}/functions/v1/planner-addin-create-task`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          board_id: boardId,
          subject: title.trim(),
          body: body.trim() || null,
          from_email: item?.fromEmail ?? null,
          from_name: item?.fromName ?? null,
          // datetime-local has no timezone; treat it as the user's local time.
          due_date: due ? new Date(due).toISOString() : null,
          internet_message_id: item?.internetMessageId ?? null,
          conversation_id: item?.conversationId ?? null,
          owner: owner || null,
          members: extraMembers.length ? extraMembers : null,
        }),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) { setError(payload?.error ?? 'Could not create the task.'); setBusy(false); return; }

      rememberBoard(boardId);
      setCreated({ ...(payload as Omit<Created, 'board_id'>), board_id: boardId });
    } catch {
      setError('Cannot reach Flowza. Check your connection.');
    }
    setBusy(false);
  };

  const signOut = async () => { await supabase.auth.signOut(); onSignedOut(); };

  if (loading) return <div className="pane"><p className="muted">Loading…</p></div>;

  if (created) {
    const link = `${APP_BASE}/o/${created.organization_id}/planner/boards/${created.board_id}?task=${created.task_id}`;
    return (
      <div className="pane">
        <header className="brand">
          <h1>Task created</h1>
          <p className="muted">{created.task_number} — {created.title}</p>
        </header>
        {/* target=_blank: a taskpane must never navigate away from itself. */}
        <a className="primary as-button" href={link} target="_blank" rel="noopener noreferrer">Open in Planner</a>
        {created.threaded && (
          <p className="muted small">
            Replies to this email that copy the board address will appear as comments on the task.
          </p>
        )}
        {item && item.attachments.length > 0 && (
          <div className="card">
            <span className="label">{item.attachments.length} attachment{item.attachments.length > 1 ? 's' : ''} not copied</span>
            <p className="muted small" style={{ margin: '4px 0 0' }}>
              {boardAddress
                ? <>Forward this email to <strong>{boardAddress}</strong> and they will be attached to the task.</>
                : <>Attachments stay in Outlook. Give this board an email address in Board Settings to forward them across.</>}
            </p>
          </div>
        )}
        <button className="link" onClick={() => { setCreated(null); setError(null); }}>
          Add this email to another board
        </button>
      </div>
    );
  }

  const noBoards = boards.length === 0;

  return (
    <div className="pane">
      <header className="brand">
        <h1>Add to Flowza</h1>
        <p className="muted small">{email}</p>
      </header>

      {loadError && <p className="error" role="alert">{loadError}</p>}

      {noBoards ? (
        <div className="card">
          <p className="muted" style={{ marginTop: 0 }}>
            No Planner boards are available to you. Ask an admin to add you to a board,
            or check that Planner is enabled for your organisation.
          </p>
        </div>
      ) : (
        <>
          <label className="field">
            <span>Board</span>
            <select value={boardId} onChange={(e) => setBoardId(e.target.value)} disabled={busy}>
              <option value="">Choose a board…</option>
              {boards.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.orgName ? `${b.name} — ${b.orgName}` : b.name}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span>Title</span>
            <input type="text" value={title} maxLength={500} disabled={busy}
              onChange={(e) => setTitle(e.target.value)} placeholder="What needs doing?" />
          </label>

          <label className="field">
            <span>Notes</span>
            <textarea rows={5} value={body} onChange={(e) => setBody(e.target.value)} disabled={busy} />
          </label>

          <label className="field">
            <span>Due date <span className="muted">(optional)</span></span>
            <input type="datetime-local" value={due} onChange={(e) => setDue(e.target.value)} disabled={busy} />
          </label>

          <label className="field">
            <span>Owner <span className="muted">(optional)</span></span>
            <select value={owner} onChange={(e) => setOwner(e.target.value)} disabled={busy || !members.length}>
              <option value="">Unassigned</option>
              {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </label>

          {members.length > 0 && (
            <fieldset className="field checks" disabled={busy}>
              <legend>Also involve <span className="muted">(optional)</span></legend>
              <div className="check-list">
                {members.filter((m) => m.id !== owner).map((m) => (
                  <label key={m.id} className="check">
                    <input type="checkbox" checked={extraMembers.includes(m.id)} onChange={() => toggleMember(m.id)} />
                    <span>{m.name}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          )}

          {item && item.attachments.length > 0 && (
            <p className="muted small" style={{ margin: 0 }}>
              <strong>{item.attachments.length} attachment{item.attachments.length > 1 ? 's' : ''}</strong> won't be copied.
              {boardAddress
                ? <> Forward this email to <strong>{boardAddress}</strong> to bring them across.</>
                : <> Give this board an email address in Board Settings to forward them across.</>}
            </p>
          )}

          <p className="muted small" style={{ margin: 0 }}>
            Add <code>#high</code>, <code>#med</code> or <code>#low</code> to the title to set priority.
          </p>

          <button className="primary" onClick={create} disabled={busy || !boardId || !title.trim()}>
            {busy ? 'Creating…' : 'Create task'}
          </button>
        </>
      )}

      {error && <p className="error" role="alert">{error}</p>}
      <button className="link" onClick={signOut} disabled={busy}>Sign out</button>
    </div>
  );
}
